/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Alert
} from '@mui/material';
import {
  Add,
  Search,
  TrendingUp,
  EmojiEvents,
  Visibility,
  Star,
  Psychology,
  Lightbulb
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile, UserProfile } from '../services/userService';
import CubeDashboard from '../components/CubeDashboard';
import { useTranslation } from 'react-i18next';

const MyDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (isAuthenticated && user?.id) {
        try {
          setLoading(true);
          const profile = await getUserProfile(user.id);
          setUserProfile(profile);
        } catch (err) {
          setError('Failed to load profile data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserProfile();
  }, [isAuthenticated, user]);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">
          {t('myDashboard.pleaseLogin', 'Please log in to view your dashboard.')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          {t('common:home', 'Home')}
        </Link>
        <Typography color="text.primary">{t('myDashboard.title', 'My Dashboard')}</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {t('myDashboard.title', 'My Dashboard')}
        </Typography>
        {user && (
          <Typography variant="subtitle1" color="text.secondary">
            {t('myDashboard.welcomeBack', 'Welcome back, {{username}}! 👋', { username: user.username }).replace(/\{\{username\}\}/g, user.username).replace(/\{\{Benutzername\}\}/g, user.username)}
          </Typography>
        )}
      </Box>

      {/* Overall Stats Section */}
      {userProfile && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {t('myDashboard.debateOverview', 'Debate Overview')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('myDashboard.overallPerformance', 'Your overall performance across all debates')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUp color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {userProfile.totalDebatesCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('myDashboard.totalDebates', 'Total Debates')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <EmojiEvents color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {userProfile.debatesWonCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('myDashboard.totalDebatesWon', 'Total Debates Won')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Visibility color="info" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {userProfile.publicDebatesCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('myDashboard.publicDebates', 'Public Debates')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Star sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {userProfile.excaliburIndex}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {t('myDashboard.excaliburIndex', 'Excalibur Index')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Quality Metrics Summary */}
            {userProfile.qualityMetrics && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <TrendingUp color="warning" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" component="div">
                        {userProfile.winRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('myDashboard.winRate', 'Win Rate')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Psychology color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" component="div">
                        {userProfile.qualityMetrics.argumentClarity.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('myDashboard.argumentClarity', 'Argument Clarity')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Lightbulb color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" component="div">
                        {userProfile.qualityMetrics.explanationStrength.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('myDashboard.explanationStrength', 'Explanation Strength')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}

      {/* Quick Actions */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          {t('myDashboard.quickActions', 'Quick Actions')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('myDashboard.getStartedWithCommonTasks', 'Get started with common tasks')}
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={RouterLink}
              to="/debates/new"
              variant="contained"
              fullWidth
              size="large"
              startIcon={<Add />}
              sx={{ py: 2 }}
            >
              {t('myDashboard.startNewDebate', 'Start New Debate')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={RouterLink}
              to="/debates/public"
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<Search />}
              sx={{ py: 2 }}
            >
              {t('myDashboard.browseDebates', 'Browse Debates')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={RouterLink}
              to="/cube"
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<Search />}
              sx={{ py: 2 }}
            >
              {t('myDashboard.exploreCube', 'Explore CUBE')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              component={RouterLink}
              to="/leaderboards"
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<EmojiEvents />}
              sx={{ py: 2 }}
            >
              {t('myDashboard.viewLeaderboard', 'View Leaderboard')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* CUBE Dashboard */}
      <CubeDashboard />
    </Container>
  );
};

export default MyDashboardPage; 